import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [-26.936698163916592, -70.0929833152042];

function escapeHtml(value = '') {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function normalizePoints(rawPoints = []) {
    return rawPoints
        .map((point) => ({
            lat: Number(point.lat),
            lng: Number(point.lng),
            title: point.title || '',
            subtitle: point.subtitle || '',
            address: point.address || '',
            variant: point.variant || 'secondary',
            icon: point.icon || '',
            is_main: Boolean(point.is_main),
            show_title: typeof point.show_title === 'boolean' ? point.show_title : true,
        }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
}

function createMarkerIcon(point) {
    const iconHtml = point.icon
        ? `<img src="${point.icon}" alt="" class="map-pin__icon" />`
        : '';

    const html = `
        <div class="map-pin map-pin--${escapeHtml(point.variant)} ${point.is_main ? 'is-main' : ''}">
            <div class="map-pin__body">
                ${iconHtml}
                ${point.subtitle ? `<span class="map-pin__subtitle">${escapeHtml(point.subtitle)}</span>` : ''}
                ${point.show_title && point.title
                        ? `<span class="map-pin__title">${escapeHtml(point.title)}</span>`
                        : ''
                    }
                </div>
            <span class="map-pin__tip" aria-hidden="true"></span>
        </div>
    `;

    return L.divIcon({
        className: '',
        html: html,
        iconSize: [190, 82],
        iconAnchor: [95, 82],
        popupAnchor: [0, -82],
    });
}

function createPopupContent(point) {
    const title = escapeHtml(point.title);
    const subtitle = escapeHtml(point.subtitle);
    const address = escapeHtml(point.address);

    return `
        <div class="map-popup">
            ${subtitle ? `<div class="map-popup__subtitle">${subtitle}</div>` : ''}
            ${title ? `<div class="map-popup__title">${title}</div>` : ''}
            ${address ? `<div class="map-popup__address">${address}</div>` : ''}
        </div>
    `;
}

function applyBounds(map, markerGroup, points, options = {}) {
    const singleZoom = Number(options.singleZoom ?? 16);
    const maxZoom = Number(options.maxZoom ?? 17);

    if (!points.length) {
        map.setView(DEFAULT_CENTER, 14);
        return;
    }

    if (points.length === 1) {
        map.setView([points[0].lat, points[0].lng], singleZoom);
        return;
    }

    map.fitBounds(markerGroup.getBounds(), {
        padding: [56, 56],
        maxZoom,
    });
}

export default function initLocationMap(selector = '#map-location') {
    const el = document.querySelector(selector);
    if (!el) return null;

    const rawPoints = JSON.parse(el.dataset.points || '[]');
    const points = normalizePoints(rawPoints);
    if (!points.length) return null;

    const singleZoom = Number(el.dataset.zoomSingle || 16);
    const maxZoom = Number(el.dataset.maxZoom || 17);

    const map = L.map(el, {
        zoomControl: true,
        scrollWheelZoom: false,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap &copy; CARTO',
        maxZoom: 24,
    }).addTo(map);

    const markers = points.map((point) => {
        const marker = L.marker([point.lat, point.lng], {
            icon: createMarkerIcon(point),
        });

        if (point.title || point.address) {
            marker.bindPopup(createPopupContent(point), {
                closeButton: true,
                autoPan: true,
            });
        }

        return marker;
    });

    const markerGroup = L.featureGroup(markers).addTo(map);

    applyBounds(map, markerGroup, points, { singleZoom, maxZoom });

    requestAnimationFrame(() => {
        map.invalidateSize();
        applyBounds(map, markerGroup, points, { singleZoom, maxZoom });
    });

    const resizeObserver = new ResizeObserver(() => {
        map.invalidateSize({ pan: false });
        applyBounds(map, markerGroup, points, { singleZoom, maxZoom });
    });

    resizeObserver.observe(el);

    return { map, markerGroup, resizeObserver };
}
